/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Card,
    Combobox,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Label,
    makeStyles,
    Option,
    Spinner,
    tokens
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { Add24Regular, CheckmarkCircle24Regular, Delete24Regular, Dismiss24Regular, Edit24Regular, Prohibited24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import type { PersonalItem } from '../../models/PersonalItem';
import type { ProjektItem } from '../../models/ProjektItem';
import type { ProjektmitarbeiterItem } from '../../models/ProjektmitarbeiterItem';
import { Projektrolle } from '../../models/ProjektRolle';
import { DataService } from '../../services/dataService';
import useConfigStore from '../../store/config';

// Helper function to check if member is pending (invited but no agreement signed)
const isPendingMember = (statusId?: number, geheimhaltungserklAkzeptiert?: Date | null): boolean => {
    return statusId === 1 && (geheimhaltungserklAkzeptiert === undefined || geheimhaltungserklAkzeptiert === null);
};

// Helper function to get status text
const getStatusText = (statusId?: number, geheimhaltungserklaerung?: boolean): string => {
    if (geheimhaltungserklaerung && geheimhaltungserklaerung === true) {
        return 'Active';
    }

    switch (statusId) {
        case 1:
            return 'Invited';
        case 2:
            return 'Active';
        case 3:
            return 'Inactive';
        default:
            return 'Unknown';
    }
};

// Helper function to convert enum to text
const getRolleName = (rolle?: Projektrolle): string => {
    if (rolle === undefined) return '';
    const roleNames: Record<Projektrolle, string> = {
        [Projektrolle.None]: '',
        [Projektrolle.Projektleiter]: 'Projektleiter',
        [Projektrolle.Projektleiterassistent]: 'Projektleiterassistent',
        [Projektrolle.Projektmitarbeiter]: 'Projektmitarbeiter',
        [Projektrolle.Projektassistent]: 'Projektassistent',
        [Projektrolle.Administrator]: 'Administrator',
        [Projektrolle.InstitutsAbteilungsleiter]: 'Instituts-/Abteilungsleiter',
        [Projektrolle.Geschäftsführung]: 'Geschäftsführung',
        [Projektrolle.Rechnungswesen]: 'Rechnungswesen',
        [Projektrolle.Controlling]: 'Controlling',
        [Projektrolle.Vertragskoordination]: 'Vertragskoordination',
        [Projektrolle.Forschungskoordination]: 'Forschungskoordination',
        [Projektrolle.Website]: 'Website',
        [Projektrolle.Personal]: 'Personal',
        [Projektrolle.Rektorat]: 'Rektorat',
        [Projektrolle.IKBManagement]: 'IKB Management',
    };
    return roleNames[rolle] || '';
};

// Helper function to get short role name for historical entries
// Helper function to get rights display text based on role
const getRightsText = (rolle?: Projektrolle): string => {
    // RolleId 2 = Projektleiterassistent = Extended rights
    // RolleId 3 = Projektmitarbeiter = Standard rights
    return rolle === Projektrolle.Projektleiterassistent ? 'Extended' : 'Standard';
};

// Helper function to get personell costs display text
const getPersonellCostsText = (isAktiv?: boolean): string => {
    return isAktiv ? 'Timetracking (PK lt. ZA)' : 'Cost unit (PK lt. KTR)';
};

// Helper function to get role abbreviation for historical entries
const getRoleAbbreviation = (rolle?: Projektrolle): string => {
    switch (rolle) {
        case Projektrolle.Projektmitarbeiter:
            return 'PMa';
        case Projektrolle.Projektassistent:
            return 'PAss';
        default:
            return getRolleName(rolle) || '';
    }
};

// Helper function to get image data URL
const getImageDataUrl = (imageData?: string | Uint8Array): string | null => {
    if (!imageData) return null;

    // If it's already a base64 string (starts with /)
    if (typeof imageData === 'string') {
        return `data:image/jpeg;base64,${imageData}`;
    }

    // If it's a Uint8Array, convert it
    if (imageData instanceof Uint8Array) {
        let binary = '';
        const len = imageData.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(imageData[i]);
        }
        const base64 = btoa(binary);
        return `data:image/jpeg;base64,${base64}`;
    }

    return null;
};

// Helper function to convert date to ISO date string (yyyy-mm-dd) without timezone conversion
const dateToIsoString = (date: Date | string | null | undefined): string | null => {
    if (!date) return null;

    // If it's already a string, extract just the date part (handle ISO format or other formats)
    if (typeof date === 'string') {
        // If it's already in yyyy-mm-dd format, return as-is
        if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
            return date.split('T')[0]; // Remove time portion if present
        }
        // For other string formats, try to parse as Date
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return null;
        date = dateObj;
    }

    if (!(date instanceof Date)) return null;
    if (isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        position: 'relative',
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        minHeight: '300px',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 'bold',
        color: tokens.colorNeutralForeground1,
        marginBottom: tokens.spacingVerticalS,
    },
    membersGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: tokens.spacingVerticalL,
    },
    memberCard: {
        padding: tokens.spacingHorizontalM,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        minHeight: '260px',
        ':hover': {
            boxShadow: tokens.shadow8,
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    memberImage: {
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        marginBottom: tokens.spacingVerticalM,
        objectFit: 'cover',
        border: `2px solid ${tokens.colorNeutralStroke1}`,
    },
    memberImageInactive: {
        filter: 'grayscale(100%)',
    },
    memberCardPending: {
        opacity: 0.6,
        backgroundColor: tokens.colorNeutralBackground2,
    },
    memberName: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: 'bold',
        color: tokens.colorNeutralForeground1,
        marginBottom: tokens.spacingVerticalXS,
    },
    memberRole: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground2,
        marginBottom: tokens.spacingVerticalXS,
        marginTop: tokens.spacingVerticalXS,
    },
    memberDates: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        marginBottom: tokens.spacingVerticalXS,
    },
    memberCost: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        marginBottom: tokens.spacingVerticalXS,
    },
    memberStatus: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: 600,
        paddingTop: tokens.spacingVerticalXS,
        paddingBottom: tokens.spacingVerticalXS,
        paddingLeft: tokens.spacingHorizontalS,
        paddingRight: tokens.spacingHorizontalS,
        borderRadius: tokens.borderRadiusSmall,
        marginTop: 'auto',
        marginBottom: tokens.spacingVerticalXS,
        borderTop: 'none',
        textAlign: 'center',
        display: 'inline-block',
        minWidth: '80px',
    },
    memberStatusActive: {
        backgroundColor: '#90EE90',
        color: '#2D5F2E',
    },
    memberStatusInactive: {
        backgroundColor: '#D3D3D3',
        color: '#666666',
    },
    addMemberCard: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
        border: `2px dashed ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    addButtonContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: tokens.spacingVerticalS,
        color: tokens.colorBrandBackground,
    },
    zustaendigerSection: {
        position: 'absolute',
        top: tokens.spacingHorizontalL,
        right: tokens.spacingHorizontalL,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: tokens.spacingVerticalS,
        padding: tokens.spacingHorizontalL,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        maxWidth: '300px',
        boxShadow: tokens.shadow8,
    },
    zustaendigerHeader: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: 'bold',
        color: tokens.colorNeutralForeground1,
        marginBottom: tokens.spacingVerticalXS,
    },
    zustaendigerCardsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: tokens.spacingVerticalS,
    },
    zustaendigerCard: {
        padding: tokens.spacingHorizontalL,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        minHeight: '180px',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusSmall,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        ':hover': {
            boxShadow: tokens.shadow4,
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    zustaendigerCardImage: {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        marginBottom: tokens.spacingVerticalM,
        objectFit: 'cover',
        border: `2px solid ${tokens.colorNeutralStroke1}`,
    },
    zustaendigerCardName: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: 'bold',
        color: tokens.colorNeutralForeground1,
        marginBottom: 0,
        wordBreak: 'break-word',
        lineHeight: '1.2',
    },
    zustaendigerCardLabel: {
        fontSize: '12px',
        color: tokens.colorNeutralForeground2,
        marginBottom: tokens.spacingVerticalS,
        fontWeight: 'bold',
    },
    zustaendigerComboboxesContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: tokens.spacingVerticalS,
        marginTop: tokens.spacingVerticalS,
    },
    zustaendigerFormField: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    zustaendigerButtonGroup: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        marginTop: tokens.spacingVerticalS,
        justifyContent: 'flex-end',
    },
    drawerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        padding: tokens.spacingHorizontalL,
    },
    detailsGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    detailLabel: {
        fontWeight: 600,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground2,
    },
    detailValue: {
        fontSize: tokens.fontSizeBase400,
        color: tokens.colorNeutralForeground1,
    },
    memberPhotoDrawer: {
        width: '120px',
        height: '120px',
        borderRadius: '50%',
        objectFit: 'cover',
        border: `2px solid ${tokens.colorNeutralStroke1}`,
        marginBottom: tokens.spacingVerticalM,
    },
    buttonGroup: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        marginTop: tokens.spacingVerticalL,
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    formLabel: {
        fontWeight: 600,
        fontSize: tokens.fontSizeBase300,
    },
    editableField: {
        marginBottom: tokens.spacingVerticalM,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    checkboxLabel: {
        marginTop: tokens.spacingVerticalS,
    },
    rowContainer: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        alignItems: 'flex-start',
    },
    rowField: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    viewSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        paddingBottom: tokens.spacingVerticalM,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    viewSectionLast: {
        borderBottom: 'none',
    },
    viewSectionTitle: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: 600,
        color: tokens.colorNeutralForeground1,
        marginBottom: tokens.spacingVerticalS,
    },
    detailRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingHorizontalM,
    },
    detailRowFull: {
        gridColumn: '1 / -1',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
});

interface MitarbeiterProjektteamProps {
    project?: ProjektItem | null;
}

const formatDate = (date: Date | string | undefined): string => {
    if (!date) return '';
    try {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return dateObj.toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
        return '';
    }
};

// Helper function to determine if an entry is currently active based on dates
const isCurrentlyActive = (member: ProjektmitarbeiterItem): boolean => {
    const now = new Date();
    const aktivVon = member.aktivVon ? new Date(member.aktivVon) : null;
    const aktivBis = member.aktivBis ? new Date(member.aktivBis) : null;

    if (!aktivVon) return false;
    if (aktivVon > now) return false; // Start date is in the future
    if (aktivBis && aktivBis < now) return false; // End date is in the past

    return true;
};

// Extended member type to include historical entries
interface MemberWithHistory extends ProjektmitarbeiterItem {
    historicalEntries?: ProjektmitarbeiterItem[];
}

// Deduplicate members by keeping only the most relevant entry per person
const deduplicateMembers = (members: ProjektmitarbeiterItem[]): MemberWithHistory[] => {
    const memberMap = new Map<number, ProjektmitarbeiterItem[]>();

    // Group by personalID
    members.forEach((member) => {
        if (!member.personalID) return;

        if (!memberMap.has(member.personalID)) {
            memberMap.set(member.personalID, []);
        }
        memberMap.get(member.personalID)?.push(member);
    });

    const result: MemberWithHistory[] = [];

    memberMap.forEach((group) => {
        // First, try to find a currently active entry
        const activeEntries = group.filter(m => isCurrentlyActive(m));

        let mainMember: ProjektmitarbeiterItem;
        if (activeEntries.length > 0) {
            // If multiple active entries, keep the one with the latest aktivVon date
            activeEntries.sort((a, b) => {
                const aDate = a.aktivVon ? new Date(a.aktivVon).getTime() : 0;
                const bDate = b.aktivVon ? new Date(b.aktivVon).getTime() : 0;
                return bDate - aDate;
            });
            mainMember = activeEntries[0];
        } else {
            // No active entries, prefer entries with actual aktivBis dates
            // Sort by: has aktivBis date first, then latest aktivBis date, then latest aktivVon date
            group.sort((a, b) => {
                const aHasDate = a.aktivBis ? 1 : 0;
                const bHasDate = b.aktivBis ? 1 : 0;

                // First, strongly prefer entries with actual aktivBis dates
                if (bHasDate !== aHasDate) {
                    return bHasDate - aHasDate;
                }

                // If both have or both don't have aktivBis dates, prefer latest aktivBis date
                const aDate = a.aktivBis ? new Date(a.aktivBis).getTime() : 0;
                const bDate = b.aktivBis ? new Date(b.aktivBis).getTime() : 0;

                if (bDate !== aDate) {
                    return bDate - aDate;
                }

                // If aktivBis dates are the same, prefer latest aktivVon date
                const aVon = a.aktivVon ? new Date(a.aktivVon).getTime() : 0;
                const bVon = b.aktivVon ? new Date(b.aktivVon).getTime() : 0;
                return bVon - aVon;
            });
            mainMember = group[0];
        }

        // Collect historical entries for roles 3 (Projektmitarbeiter/PMa) and 4 (Projektassistent/PAss)
        // Use guid to identify unique entries since itemId may be the same (0) for multiple entries
        const historicalEntries = group.filter(
            (m) => m.guid !== mainMember.guid && (m.rolle === 3 || m.rolle === 4)
        );

        const memberWithHistory: MemberWithHistory = {
            ...mainMember,
            historicalEntries: historicalEntries.length > 0 ? historicalEntries : undefined,
        };

        result.push(memberWithHistory);
    });

    return result;
};

const MitarbeiterProjektteamContent: React.FC<{ project: ProjektItem | null }> = ({ project }) => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const { refreshProject } = useProject();

    // State management
    const [projectMembers, setProjectMembers] = useState<ProjektmitarbeiterItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Drawer state
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState<ProjektmitarbeiterItem | null>(null);
    const [drawerMode, setDrawerMode] = useState<'view' | 'add'>('view');
    const [isEditMode, setIsEditMode] = useState(false);

    // Edit state
    const [editedMember, setEditedMember] = useState<Partial<ProjektmitarbeiterItem> | null>(null);

    // Add/Edit state
    const [availableMitarbeiter, setAvailableMitarbeiter] = useState<ProjektmitarbeiterItem[]>([]);
    const [memberSearchResults, setMemberSearchResults] = useState<ProjektmitarbeiterItem[]>([]);
    const [selectedSearchMember, setSelectedSearchMember] = useState<ProjektmitarbeiterItem | null>(null);
    const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [mitarbeiterLoaded, setMitarbeiterLoaded] = useState(false);

    // Zuständiger state
    const [selectedControlling, setSelectedControlling] = useState<PersonalItem | null>(null);
    const [selectedForschung, setSelectedForschung] = useState<PersonalItem | null>(null);
    const [editingControlling, setEditingControlling] = useState<PersonalItem | null>(null);
    const [editingForschung, setEditingForschung] = useState<PersonalItem | null>(null);
    const [controllingSearchResults, setControllingSearchResults] = useState<PersonalItem[]>([]);
    const [forschungSearchResults, setForschungSearchResults] = useState<PersonalItem[]>([]);
    const [availableStaff, setAvailableStaff] = useState<PersonalItem[]>([]);
    const [staffLoaded, setStaffLoaded] = useState(false);
    const [zustaendigerSearchTimeout, setZustaendigerSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [isSavingZustaendiger, setIsSavingZustaendiger] = useState(false);
    const [isEditingControllingType, setIsEditingControllingType] = useState(false);
    const [isEditingForschungType, setIsEditingForschungType] = useState(false);

    // Get user config and info for permission checks
    const { selectedRole } = useConfigStore((state) => state.roleConfig);
    const { userInfo } = useConfigStore((state) => state.userConfig);

    // User can edit/add members if they are:
    // - Global admin (isUserAdmin)
    // - Project Leiter (isBenutzerProjektLeiter)
    // - Project Leiter Assistant (isBenutzerProjektleiterAssistent)
    // - Selected role is Administratoren
    const canEditMembers = useMemo(() => {
        if (!userInfo || !project) return false;
        return project.isBenutzerProjektLeiter || project.isBenutzerProjektleiterAssistent || selectedRole === 'Administratoren';
    }, [userInfo, project, selectedRole]);

    // Handle activate member
    const handleActivateMember = async () => {
        if (!selectedMember || !dataService) {
            return;
        }
        try {
            setIsLoading(true);
            await dataService.activateProjektmitarbeiter(selectedMember.itemIdInactive);
            const updatedMembers = await fetchProjectMembers();
            // Update selectedMember with refreshed data
            const refreshedMember = updatedMembers.find(m => m.personalID === selectedMember.personalID);
            if (refreshedMember) {
                setSelectedMember(refreshedMember);
            }
        } catch (error) {
            console.error('Error activating member:', error);
            setError('Failed to activate member');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle deactivate member
    const handleDeactivateMember = async () => {
        if (!selectedMember || !dataService) {
            return;
        }
        try {
            setIsLoading(true);
            await dataService.deactivateProjektmitarbeiter(selectedMember.itemId);
            const updatedMembers = await fetchProjectMembers();
            // Update selectedMember with refreshed data
            const refreshedMember = updatedMembers.find(m => m.personalID === selectedMember.personalID);
            if (refreshedMember) {
                setSelectedMember(refreshedMember);
            }
        } catch (error) {
            console.error('Error deactivating member:', error);
            setError('Failed to deactivate member');
        } finally {
            setIsLoading(false);
        }
    };

    // Create dataService instance using MSAL context
    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch project members
    const fetchProjectMembers = useCallback(async () => {
        if (!project?.projektID || !dataService) {
            return [];
        }

        try {
            setIsLoading(true);
            setError(null);
            // Fetch all project members using the API
            const members = await dataService.getProjektmitarbeiterListe(project.projektID);
            setProjectMembers(members);
            return members;
        } catch (err) {
            console.error('Error loading project members:', err);
            setError('Error loading project members');
            setProjectMembers([]);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [project?.projektID, dataService]);

    useEffect(() => {
        fetchProjectMembers();
    }, [fetchProjectMembers]);

    // Initialize zuständiger from project PersonalItem properties
    useEffect(() => {
        if (project) {
            const controlling = project.zustaendigerControlling || null;
            const forschung = project.zustaendigerForschung || null;
            setSelectedControlling(controlling);
            setEditingControlling(controlling);
            setSelectedForschung(forschung);
            setEditingForschung(forschung);
        }
    }, [project]);

    // Load all available staff for zuständiger selection
    const loadAvailableStaff = useCallback(async () => {
        if (!dataService || staffLoaded) {
            return;
        }

        try {
            const staff = await dataService.getMitarbeiter();
            // Convert ProjektmitarbeiterItem to PersonalItem for display
            const personalItems = staff.map((item) => ({
                personalID: item.personalID,
                benutzername: item.benutzername || '',
                email: item.email || '',
                vorname: item.vorname || '',
                nachname: item.nachname || '',
                titel: item.titel || '',
                titelAfter: item.titelAfter || '',
                fullName: item.fullName || `${item.vorname} ${item.nachname}`,
                fullNameWithTitles: item.fullNameWithTitles || '',
                fullNameUppercase: item.fullNameUppercase || '',
                fullNameUppercaseWithTitles: item.fullNameUppercaseWithTitles || '',
                presenceStatus: '',
                title: '',
                description: '',
                foto: item.foto,
                imageBinary: item.imageBinary,
                value: item.personalID.toString(),
                text: item.fullName || `${item.vorname} ${item.nachname}`,
                selected: false,
                code: 0,
                visibility: '',
                currentDepartment: '',
                emailVorgesetzter: '',
                isAssignableAsProjectManager: false,
                isAssignableAsProjectMember: false,
                isAssignableAsProjectStudent: false,
                adresse: '',
                plz: '',
                ort: '',
                funktion: '',
            } as PersonalItem));
            setAvailableStaff(personalItems);
            setStaffLoaded(true);
        } catch (error) {
            console.error('Error loading staff:', error);
            setAvailableStaff([]);
        }
    }, [dataService, staffLoaded]);

    // Handle controlling staff search
    const handleControllingSearch = useCallback(
        (searchTerm: string) => {
            if (zustaendigerSearchTimeout) {
                clearTimeout(zustaendigerSearchTimeout);
            }

            if (searchTerm.length >= 2) {
                const timeout = setTimeout(() => {
                    try {
                        const results = availableStaff.filter(
                            (staff) =>
                                staff.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                staff.vorname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                staff.nachname?.toLowerCase().includes(searchTerm.toLowerCase())
                        );
                        setControllingSearchResults(results);
                    } catch (error) {
                        console.error('Error searching staff:', error);
                        setControllingSearchResults([]);
                    }
                }, 300);
                setZustaendigerSearchTimeout(timeout);
            } else {
                setControllingSearchResults([]);
            }
        },
        [availableStaff, zustaendigerSearchTimeout],
    );

    // Handle forschung staff search
    const handleForschungSearch = useCallback(
        (searchTerm: string) => {
            if (zustaendigerSearchTimeout) {
                clearTimeout(zustaendigerSearchTimeout);
            }

            if (searchTerm.length >= 2) {
                const timeout = setTimeout(() => {
                    try {
                        const results = availableStaff.filter(
                            (staff) =>
                                staff.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                staff.vorname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                staff.nachname?.toLowerCase().includes(searchTerm.toLowerCase())
                        );
                        setForschungSearchResults(results);
                    } catch (error) {
                        console.error('Error searching staff:', error);
                        setForschungSearchResults([]);
                    }
                }, 300);
                setZustaendigerSearchTimeout(timeout);
            } else {
                setForschungSearchResults([]);
            }
        },
        [availableStaff, zustaendigerSearchTimeout],
    );

    // Reset editing state when not in edit mode
    useEffect(() => {
        if (!isEditingControllingType) {
            setEditingControlling(selectedControlling);
            setControllingSearchResults([]);
        }
        if (!isEditingForschungType) {
            setEditingForschung(selectedForschung);
            setForschungSearchResults([]);
        }
    }, [isEditingControllingType, isEditingForschungType, selectedControlling, selectedForschung]);

    // Save zuständiger assignments
    const handleSaveZustaendiger = async () => {
        if (!project?.projektID || !dataService) {
            return;
        }

        try {
            setIsSavingZustaendiger(true);

            // Save controlling assignment (type=true)
            if (editingControlling?.personalID !== selectedControlling?.personalID) {
                await dataService.changeZustaendiger(
                    project.projektID,
                    true,
                    editingControlling?.personalID
                );
            }

            // Save forschung assignment (type=false)
            if (editingForschung?.personalID !== selectedForschung?.personalID) {
                await dataService.changeZustaendiger(
                    project.projektID,
                    false,
                    editingForschung?.personalID
                );
            }

            // Update the selected values
            setSelectedControlling(editingControlling);
            setSelectedForschung(editingForschung);
            setIsEditingControllingType(false);
            setIsEditingForschungType(false);

            // Reload project to get updated zuständiger data
            if (refreshProject) {
                await refreshProject();
            }

            setError(null);
        } catch (err) {
            console.error('Error saving zuständiger:', err);
            setError('Failed to save staff assignments');
        } finally {
            setIsSavingZustaendiger(false);
        }
    };

    // Cancel zuständiger changes for Controlling
    const handleCancelControlling = () => {
        setEditingControlling(selectedControlling);
        setControllingSearchResults([]);
        setIsEditingControllingType(false);
    };

    // Cancel zuständiger changes for Forschung
    const handleCancelForschung = () => {
        setEditingForschung(selectedForschung);
        setForschungSearchResults([]);
        setIsEditingForschungType(false);
    };

    // Load staff when component mounts
    useEffect(() => {
        if (!staffLoaded && dataService) {
            loadAvailableStaff();
        }
    }, [dataService, staffLoaded, loadAvailableStaff]);

    // Load all available mitarbeiter for search
    const loadAvailableMitarbeiter = useCallback(async () => {
        if (!dataService || mitarbeiterLoaded) {
            return;
        }

        try {
            const mitarbeiter = await dataService.getMitarbeiter();
            setAvailableMitarbeiter(mitarbeiter);
            setMitarbeiterLoaded(true);
        } catch (error) {
            console.error('Error loading mitarbeiter:', error);
            setAvailableMitarbeiter([]);
        }
    }, [dataService, mitarbeiterLoaded]);

    // Handle member search
    const handleMemberSearch = useCallback(
        (searchTerm: string) => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }

            if (searchTerm.length >= 2) {
                const timeout = setTimeout(() => {
                    try {
                        // Filter available mitarbeiter based on search term
                        const results = availableMitarbeiter.filter(
                            (member) =>
                                `${member.vorname} ${member.nachname}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                member.vorname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                member.nachname?.toLowerCase().includes(searchTerm.toLowerCase())
                        );
                        setMemberSearchResults(results);
                    } catch (error) {
                        console.error('Error searching members:', error);
                        setMemberSearchResults([]);
                    }
                }, 300);
                setSearchTimeout(timeout);
            } else {
                setMemberSearchResults([]);
            }
        },
        [availableMitarbeiter, searchTimeout],
    );

    // Open drawer for viewing/editing
    const handleOpenDrawer = (member: ProjektmitarbeiterItem | null, mode: 'view' | 'add') => {
        setSelectedMember(member);
        setDrawerMode(mode);
        setIsDrawerOpen(true);
        setSelectedSearchMember(null);
        setMemberSearchResults([]);
        // Load mitarbeiter when opening add drawer
        if (mode === 'add') {
            loadAvailableMitarbeiter();
        }
    };

    // Close drawer
    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
        setSelectedMember(null);
        setDrawerMode('view');
        setIsEditMode(false);
        setEditedMember(null);
        setSelectedSearchMember(null);
        setMemberSearchResults([]);
    };

    // Enter edit mode
    const handleEnterEditMode = () => {
        if (selectedMember) {
            setEditedMember({
                ...selectedMember,
            });
            setIsEditMode(true);
        }
    };

    // Cancel edit mode
    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditedMember(null);
    };

    // Update edited field
    const handleEditFieldChange = (field: string, value: unknown) => {
        setEditedMember((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // Save edited member
    const handleSaveEdit = async () => {
        if (!selectedMember || !editedMember || !dataService) {
            return;
        }
        try {
            setIsLoading(true);
            const updatedMember: ProjektmitarbeiterItem = {
                ...selectedMember,
                aktivVon: editedMember.aktivVon ?? selectedMember.aktivVon,
                aktivBis: editedMember.aktivBis ?? selectedMember.aktivBis,
                isAktiv: editedMember.isAktiv ?? selectedMember.isAktiv,
                usePersonalkosten: editedMember.usePersonalkosten ?? selectedMember.usePersonalkosten,
                rolle: editedMember.rolle ?? selectedMember.rolle,
            };

            // Convert dates to ISO format strings to avoid timezone offset issues
            const memberToSend = {
                ...updatedMember,
                aktivVon: dateToIsoString(updatedMember.aktivVon as any) ?? updatedMember.aktivVon,
                aktivBis: updatedMember.aktivBis ? dateToIsoString(updatedMember.aktivBis as any) : null,
            };

            // Determine erweiterteRechte based on the role
            // True if extended rights (role 2), False if standard rights (role 3)
            const erweiterteRechte = updatedMember.rolle === Projektrolle.Projektleiterassistent ? true : false;

            await dataService.updateProjektmitarbeiter(memberToSend as any, erweiterteRechte);
            handleCancelEdit();
            await fetchProjectMembers();

            // Update selectedMember with the edited values to show fresh data in drawer
            setSelectedMember({
                ...selectedMember,
                aktivVon: editedMember.aktivVon ?? selectedMember.aktivVon,
                aktivBis: editedMember.aktivBis ?? selectedMember.aktivBis,
                isAktiv: editedMember.isAktiv ?? selectedMember.isAktiv,
                usePersonalkosten: editedMember.usePersonalkosten ?? selectedMember.usePersonalkosten,
                rolle: editedMember.rolle ?? selectedMember.rolle,
            });
        } catch (error) {
            console.error('Error saving member:', error);
            setError('Failed to save member changes');
        } finally {
            setIsLoading(false);
        }
    };

    // Delete member (remove)
    const handleDeleteMember = async () => {
        if (!selectedMember || !dataService || !project?.projektID) {
            return;
        }
        try {
            setIsLoading(true);
            await dataService.removeProjektmitarbeiter(project.projektID, selectedMember.itemId);
            handleCloseDrawer();
            await fetchProjectMembers();
        } catch (error) {
            console.error('Error deleting member:', error);
            setError('Failed to remove member');
        } finally {
            setIsLoading(false);
        }
    };

    // Save new member assignment
    const handleSaveMemberAssignment = async () => {
        if (!selectedSearchMember || !dataService || !project?.projektID) {
            return;
        }
        try {
            setIsLoading(true);
            const addRequest = {
                projektID: project.projektID,
                userID: selectedSearchMember.personalID,
                rolle: Projektrolle.Projektmitarbeiter,
                forceAssignment: false,
                sendGehmErkl: false,
                addedByKalkulation: false,
            };
            await dataService.addProjektmitarbeiter(addRequest);
            await fetchProjectMembers();
            handleCloseDrawer();
        } catch (error) {
            console.error('Error adding member:', error);
            setError('Failed to add member to project');
        } finally {
            setIsLoading(false);
        }
    };

    if (!project) return null;

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading project team..." />
                </div>
            </div>
        );
    }

    // Separate project manager from other members
    const projectManager = project.projektleiter;
    const otherMembers = projectMembers.filter((m) => m.personalID !== projectManager?.personalID);

    // Deduplicate members to show only the most recent entry per person
    const deduplicatedMembers = deduplicateMembers(otherMembers);

    // Separate active and inactive members
    const activeMembers = deduplicatedMembers.filter((m) => m.isAktiv);
    const inactiveMembers = deduplicatedMembers.filter((m) => !m.isAktiv);
    const renderMemberCard = (member: MemberWithHistory) => {
        const foto = member.foto || member.imageBinary;
        const fotoUrl = getImageDataUrl(foto);
        const aktivVonDate = formatDate(member.aktivVon);
        const roleName = getRolleName(member.rolle);

        // Determine status with proper priority:
        // 1. If isAktiv is false -> Inactive
        // 2. If aktivBis is in the past -> Inactive
        // 3. If geheimhaltungserklAkzeptiert is null/empty -> Invited
        // 4. Otherwise use getStatusText based on statusId
        let statusText = 'Unknown';
        if (!member.isAktiv || (member.aktivBis && new Date(member.aktivBis) < new Date())) {
            statusText = 'Inactive';
        } else if (!member.geheimhaltungserklAkzeptiert) {
            statusText = 'Invited';
        } else {
            statusText = getStatusText(member.statusId, true);
        }

        // For inactive members without an aktivBis date, look for a fallback date from other entries
        let displayActivBis = member.aktivBis;
        if (statusText === 'Inactive' && !displayActivBis && member.personalID) {
            // Find the most recent aktivBis date from other entries with the same personalID
            const fallbackDates = projectMembers
                .filter((m) => m.personalID === member.personalID && m.aktivBis && m.guid !== member.guid)
                .sort((a, b) => {
                    const aDate = a.aktivBis ? new Date(a.aktivBis).getTime() : 0;
                    const bDate = b.aktivBis ? new Date(b.aktivBis).getTime() : 0;
                    return bDate - aDate;
                });

            if (fallbackDates.length > 0) {
                displayActivBis = fallbackDates[0].aktivBis;
            } else {
                // If still no date found and member is inactive, use today's date as fallback
                displayActivBis = new Date();
            }
        }

        // For inactive members, always show the aktivBis date (no "ongoing")
        // For active members, show "ongoing" if no aktivBis
        const aktivBisDate = displayActivBis ? formatDate(displayActivBis) : (statusText === 'Inactive' ? '' : 'ongoing');

        const isPending = isPendingMember(member.statusId, member.geheimhaltungserklAkzeptiert);
        const personellCostsText = getPersonellCostsText(member.usePersonalkosten);
        const isActive = statusText === 'Active';

        return (
            <Card key={member.guid} className={`${styles.memberCard} ${isPending ? styles.memberCardPending : ''}`} onClick={() => handleOpenDrawer(member, 'view')}>
                {fotoUrl && (
                    <img
                        src={fotoUrl}
                        alt={member.fullName}
                        className={`${styles.memberImage} ${!member.isAktiv ? styles.memberImageInactive : ''} ${isPending ? styles.memberImageInactive : ''}`}
                    />
                )}
                <div className={styles.memberName}>{member.fullName || `${member.vorname} ${member.nachname}`}</div>
                {roleName && <div className={styles.memberRole}>{roleName}</div>}
                <div className={styles.memberDates}>
                    {statusText === 'Inactive' ? (
                        <div><strong>Inactive from:</strong> {aktivBisDate || 'N/A'}</div>
                    ) : (
                        <div style={{ fontSize: tokens.fontSizeBase200 }}>
                            <strong>Active:</strong> {aktivVonDate} - {aktivBisDate}
                        </div>
                    )}
                </div>
                {/* Display historical entries for roles 3 (PMa) and 4 (PAss) */}
                {member.historicalEntries && member.historicalEntries.length > 0 && (
                    <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalXS, paddingTop: tokens.spacingVerticalXS, borderTop: `1px solid ${tokens.colorNeutralStroke2}`, width: '100%' }}>
                        {member.historicalEntries.map((histEntry, idx) => {
                            const roleAbbr = getRoleAbbreviation(histEntry.rolle);
                            const histVonDate = formatDate(histEntry.aktivVon);
                            const histBisDate = histEntry.aktivBis ? formatDate(histEntry.aktivBis) : 'ongoing';
                            return (
                                <div key={idx} style={{ marginTop: tokens.spacingVerticalXS }}>
                                    <strong>{roleAbbr}:</strong> {histVonDate} - {histBisDate}
                                </div>
                            );
                        })}
                    </div>
                )}
                {personellCostsText && <div className={styles.memberCost}>{personellCostsText}</div>}
                <div className={`${styles.memberStatus} ${isActive ? styles.memberStatusActive : styles.memberStatusInactive}`}>{statusText}</div>
            </Card>
        );
    };

    return (
        <div className={styles.container}>
            {error && <div style={{ color: 'red', padding: tokens.spacingHorizontalM }}>{error}</div>}

            {/* Zuständiger Section */}
            <div className={styles.zustaendigerSection}>
                {/* Controlling Card/Edit - Hidden when editing Forschung */}
                {!isEditingForschungType && (
                    <>
                        {!isEditingControllingType ? (
                            <Card
                                className={styles.zustaendigerCard}
                                onClick={() => {
                                    setIsEditingControllingType(true);
                                    loadAvailableStaff();
                                }}
                            >
                                <div className={styles.zustaendigerCardLabel}>Zuständiger Controlling</div>
                                {selectedControlling ? (
                                    <>
                                        {(() => {
                                            const foto = selectedControlling.foto || selectedControlling.imageBinary;
                                            const fotoUrl = getImageDataUrl(foto);
                                            return fotoUrl ? (
                                                <img
                                                    src={fotoUrl}
                                                    alt={selectedControlling.fullName}
                                                    className={styles.zustaendigerCardImage}
                                                />
                                            ) : null;
                                        })()}
                                        <div className={styles.zustaendigerCardName}>{selectedControlling.fullName}</div>
                                    </>
                                ) : (
                                    <div className={styles.zustaendigerCardName} style={{ color: tokens.colorNeutralForeground3, fontSize: '10px' }}>Click to assign</div>
                                )}
                            </Card>
                        ) : (
                            <div className={styles.zustaendigerFormField}>
                                <Label style={{ fontSize: '11px' }}>Zuständiger Controlling</Label>
                                <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                                    <Combobox
                                        placeholder="Search..."
                                        onOptionSelect={(_, option) => {
                                            const personalID = parseInt(option.optionValue || '0');
                                            const staff = controllingSearchResults.find((s) => s.personalID === personalID);
                                            if (staff) {
                                                setEditingControlling(staff);
                                            }
                                        }}
                                        onChange={(ev) => {
                                            const value = (ev.target as HTMLInputElement).value;
                                            handleControllingSearch(value);
                                            if (value.length === 0) {
                                                setEditingControlling(selectedControlling);
                                            }
                                        }}
                                        appearance="outline"
                                        size="small"
                                        style={{ flex: 1 }}
                                    >
                                        {controllingSearchResults.map((staff) => (
                                            <Option
                                                key={staff.personalID}
                                                value={staff.personalID.toString()}
                                                text={staff.fullName || `${staff.vorname} ${staff.nachname}`}
                                            >
                                                {staff.fullName || `${staff.vorname} ${staff.nachname}`}
                                            </Option>
                                        ))}
                                    </Combobox>
                                    {editingControlling && (
                                        <Button
                                            icon={<Delete24Regular />}
                                            onClick={() => setEditingControlling(null)}
                                            appearance="outline"
                                            size="small"
                                            title="Remove"
                                        />
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
                                    <Button
                                        onClick={handleSaveZustaendiger}
                                        disabled={isSavingZustaendiger}
                                        appearance="primary"
                                        size="small"
                                        style={{ flex: 1 }}
                                    >
                                        {isSavingZustaendiger ? 'Saving...' : 'Save'}
                                    </Button>
                                    <Button onClick={handleCancelControlling} disabled={isSavingZustaendiger} size="small" style={{ flex: 1 }}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Forschung Card/Edit - Hidden when editing Controlling */}
                {!isEditingControllingType && (
                    <>
                        {!isEditingForschungType ? (
                            <Card
                                className={styles.zustaendigerCard}
                                onClick={() => {
                                    setIsEditingForschungType(true);
                                    loadAvailableStaff();
                                }}
                            >
                                <div className={styles.zustaendigerCardLabel}>Zuständiger Forschung</div>
                                {selectedForschung ? (
                                    <>
                                        {(() => {
                                            const foto = selectedForschung.foto || selectedForschung.imageBinary;
                                            const fotoUrl = getImageDataUrl(foto);
                                            return fotoUrl ? (
                                                <img
                                                    src={fotoUrl}
                                                    alt={selectedForschung.fullName}
                                                    className={styles.zustaendigerCardImage}
                                                />
                                            ) : null;
                                        })()}
                                        <div className={styles.zustaendigerCardName}>{selectedForschung.fullName}</div>
                                    </>
                                ) : (
                                    <div className={styles.zustaendigerCardName} style={{ color: tokens.colorNeutralForeground3, fontSize: '10px' }}>Click to assign</div>
                                )}
                            </Card>
                        ) : (
                            <div className={styles.zustaendigerFormField}>
                                <Label style={{ fontSize: '11px' }}>Zuständiger Forschung</Label>
                                <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                                    <Combobox
                                        placeholder="Search..."
                                        onOptionSelect={(_, option) => {
                                            const personalID = parseInt(option.optionValue || '0');
                                            const staff = forschungSearchResults.find((s) => s.personalID === personalID);
                                            if (staff) {
                                                setEditingForschung(staff);
                                            }
                                        }}
                                        onChange={(ev) => {
                                            const value = (ev.target as HTMLInputElement).value;
                                            handleForschungSearch(value);
                                            if (value.length === 0) {
                                                setEditingForschung(selectedForschung);
                                            }
                                        }}
                                        appearance="outline"
                                        size="small"
                                        style={{ flex: 1 }}
                                    >
                                        {forschungSearchResults.map((staff) => (
                                            <Option
                                                key={staff.personalID}
                                                value={staff.personalID.toString()}
                                                text={staff.fullName || `${staff.vorname} ${staff.nachname}`}
                                            >
                                                {staff.fullName || `${staff.vorname} ${staff.nachname}`}
                                            </Option>
                                        ))}
                                    </Combobox>
                                    {editingForschung && (
                                        <Button
                                            icon={<Delete24Regular />}
                                            onClick={() => setEditingForschung(null)}
                                            appearance="outline"
                                            size="small"
                                            title="Remove"
                                        />
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
                                    <Button
                                        onClick={handleSaveZustaendiger}
                                        disabled={isSavingZustaendiger}
                                        appearance="primary"
                                        size="small"
                                        style={{ flex: 1 }}
                                    >
                                        {isSavingZustaendiger ? 'Saving...' : 'Save'}
                                    </Button>
                                    <Button onClick={handleCancelForschung} disabled={isSavingZustaendiger} size="small" style={{ flex: 1 }}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Project Manager Section */}
            {projectManager && (
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Project Manager</div>
                    <div className={styles.membersGrid}>{renderMemberCard(projectManager as MemberWithHistory)}</div>
                </div>
            )}

            {/* Active Project Members Section */}
            {activeMembers.length > 0 && (
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Active Project Members</div>
                    <div className={styles.membersGrid}>
                        {activeMembers.map((member) => renderMemberCard(member))}
                        {canEditMembers && (
                            <Card className={styles.addMemberCard} onClick={() => handleOpenDrawer(null, 'add')}>
                                <div className={styles.addButtonContent}>
                                    <Add24Regular fontSize={32} />
                                    <span>Add Member</span>
                                </div>
                            </Card>
                        )}
                    </div>
                </div>
            )}

            {/* Inactive Project Members Section */}
            {inactiveMembers.length > 0 && (
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Inactive Project Members</div>
                    <div className={styles.membersGrid}>{inactiveMembers.map((member) => renderMemberCard(member))}</div>
                </div>
            )}

            {/* Add Member Button (if no members) */}
            {activeMembers.length === 0 && !projectManager && canEditMembers && (
                <div className={styles.section}>
                    <Card className={styles.addMemberCard} onClick={() => handleOpenDrawer(null, 'add')}>
                        <div className={styles.addButtonContent}>
                            <Add24Regular fontSize={32} />
                            <span>Add Member</span>
                        </div>
                    </Card>
                </div>
            )}

            {/* Detail/Add Drawer */}
            <Drawer open={isDrawerOpen} size="medium" onOpenChange={(_, { open }) => !open && handleCloseDrawer()} position="end">
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={handleCloseDrawer} />
                        }
                    >
                        {drawerMode === 'view' ? 'Team Member Details' : 'Add Team Member'}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    <div className={styles.drawerContent}>
                        {drawerMode === 'view' && selectedMember && (
                            <>
                                {/* Photo Section */}
                                {(() => {
                                    const foto = selectedMember.foto || selectedMember.imageBinary;
                                    const fotoUrl = getImageDataUrl(foto);
                                    return fotoUrl ? (
                                        <img
                                            src={fotoUrl}
                                            alt={selectedMember.fullName}
                                            className={styles.memberPhotoDrawer}
                                        />
                                    ) : null;
                                })()}

                                {isEditMode ? (
                                    <>
                                        {/* Basic Information - Non-Editable */}
                                        <div className={styles.viewSection}>
                                            <div className={styles.viewSectionTitle}>Basic Information</div>
                                            <div className={styles.detailsGroup}>
                                                <div>
                                                    <div className={styles.detailLabel}>Name</div>
                                                    <div className={styles.detailValue}>{selectedMember.fullName}</div>
                                                </div>
                                            </div>

                                            <div className={styles.detailsGroup}>
                                                <div>
                                                    <div className={styles.detailLabel}>Project Role</div>
                                                    <div className={styles.detailValue}>{getRolleName(selectedMember.rolle)}</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Team Assignment - Editable */}
                                        <div className={styles.viewSection}>
                                            <div className={styles.viewSectionTitle}>Team Assignment</div>

                                            {/* Rights Dropdown - Only show if NOT Projektleiter */}
                                            {selectedMember.rolle !== Projektrolle.Projektleiter && (
                                                <div className={styles.editableField}>
                                                    <Label required>Rights</Label>
                                                    <Combobox
                                                        value={editedMember?.rolle === Projektrolle.Projektleiterassistent ? 'Extended' : 'Standard'}
                                                        onOptionSelect={(_, option) => {
                                                            const newRole = option.optionValue === 'extended' ? Projektrolle.Projektleiterassistent : Projektrolle.Projektmitarbeiter;
                                                            handleEditFieldChange('rolle', newRole);
                                                        }}
                                                        appearance="outline"
                                                    >
                                                        <Option value="standard" text="Standard">Standard</Option>
                                                        <Option value="extended" text="Extended">Extended</Option>
                                                    </Combobox>
                                                </div>
                                            )}

                                            {/* Date fields in one row */}
                                            <div className={styles.rowContainer}>
                                                <div className={styles.rowField}>
                                                    <Label required>Part of team from</Label>
                                                    <DatePicker
                                                        value={editedMember?.aktivVon ? new Date(editedMember.aktivVon) : undefined}
                                                        onSelectDate={(date) => {
                                                            handleEditFieldChange('aktivVon', date);
                                                        }}
                                                        placeholder="Select date"
                                                        formatDate={(date) => {
                                                            if (!date) return '';
                                                            const day = String(date.getDate()).padStart(2, '0');
                                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                                            const year = date.getFullYear();
                                                            return `${day}.${month}.${year}`;
                                                        }}
                                                        parseDateFromString={(dateString) => {
                                                            const parts = dateString.split('.');
                                                            if (parts.length === 3) {
                                                                const date = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                                                                return date;
                                                            }
                                                            return null;
                                                        }}
                                                    />
                                                </div>
                                                <div className={styles.rowField}>
                                                    <Label required>to</Label>
                                                    <DatePicker
                                                        value={editedMember?.aktivBis ? new Date(editedMember.aktivBis) : undefined}
                                                        onSelectDate={(date) => {
                                                            handleEditFieldChange('aktivBis', date);
                                                        }}
                                                        placeholder="Select date"
                                                        formatDate={(date) => {
                                                            if (!date) return '';
                                                            const day = String(date.getDate()).padStart(2, '0');
                                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                                            const year = date.getFullYear();
                                                            return `${day}.${month}.${year}`;
                                                        }}
                                                        parseDateFromString={(dateString) => {
                                                            const parts = dateString.split('.');
                                                            if (parts.length === 3) {
                                                                const date = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                                                                return date;
                                                            }
                                                            return null;
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Personnel Costs - Editable */}
                                        <div className={`${styles.viewSection} ${styles.viewSectionLast}`}>
                                            <div className={styles.viewSectionTitle}>Personnel Costs</div>

                                            <div className={styles.editableField}>
                                                <Label required>Personnel Costs</Label>
                                                <Combobox
                                                    value={editedMember?.usePersonalkosten ? 'Timetracking (PK lt. ZA)' : 'Cost unit (PK lt. KTR)'}
                                                    onOptionSelect={(_, option) => {
                                                        handleEditFieldChange('usePersonalkosten', option.optionValue === 'timetracking');
                                                    }}
                                                    appearance="outline"
                                                >
                                                    <Option value="timetracking" text="Timetracking (PK lt. ZA)">Timetracking (PK lt. ZA)</Option>
                                                    <Option value="costunit" text="Cost unit (PK lt. KTR)">Cost unit (PK lt. KTR)</Option>
                                                </Combobox>
                                            </div>
                                        </div>

                                        <div className={styles.buttonGroup}>
                                            <Button onClick={handleSaveEdit} appearance="primary">
                                                Save
                                            </Button>
                                            <Button onClick={handleCancelEdit}>Cancel</Button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* Basic Information */}
                                        <div className={styles.viewSection}>
                                            <div className={styles.viewSectionTitle}>Basic Information</div>
                                            <div className={styles.detailsGroup}>
                                                <div>
                                                    <div className={styles.detailLabel}>Name</div>
                                                    <div className={styles.detailValue}>{selectedMember.fullName}</div>
                                                </div>
                                            </div>

                                            {selectedMember.rolle !== undefined && (
                                                <div className={styles.detailsGroup}>
                                                    <div>
                                                        <div className={styles.detailLabel}>Project Role</div>
                                                        <div className={styles.detailValue}>{getRolleName(selectedMember.rolle)}</div>
                                                    </div>
                                                </div>
                                            )}

                                            {selectedMember.email && (
                                                <div className={styles.detailsGroup}>
                                                    <div>
                                                        <div className={styles.detailLabel}>Email</div>
                                                        <div className={styles.detailValue}>{selectedMember.email}</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Team Assignment */}
                                        <div className={styles.viewSection}>
                                            <div className={styles.viewSectionTitle}>Team Assignment</div>
                                            <div className={styles.detailRow}>
                                                <div className={styles.detailsGroup}>
                                                    <div>
                                                        <div className={styles.detailLabel}>Part of team from</div>
                                                        <div className={styles.detailValue}>{formatDate(selectedMember.aktivVon)}</div>
                                                    </div>
                                                </div>
                                                {selectedMember.aktivBis && (
                                                    <div className={styles.detailsGroup}>
                                                        <div>
                                                            <div className={styles.detailLabel}>to</div>
                                                            <div className={styles.detailValue}>{formatDate(selectedMember.aktivBis)}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {selectedMember.rolle !== Projektrolle.Projektleiter && (
                                                <div className={styles.detailsGroup}>
                                                    <div>
                                                        <div className={styles.detailLabel}>Rights</div>
                                                        <div className={styles.detailValue}>{getRightsText(selectedMember.rolle)}</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Personnel Costs */}
                                        <div className={styles.viewSection}>
                                            <div className={styles.viewSectionTitle}>Personnel Costs</div>
                                            <div className={styles.detailsGroup}>
                                                <div>
                                                    <div className={styles.detailLabel}>Personell Costs</div>
                                                    <div className={styles.detailValue}>{getPersonellCostsText(selectedMember.usePersonalkosten)}</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Further Project Roles */}
                                        {(() => {
                                            // Find the member with historical entries in deduplicatedMembers
                                            const memberWithHistory = deduplicatedMembers.find((m) => m.personalID === selectedMember.personalID);
                                            if (memberWithHistory?.historicalEntries && memberWithHistory.historicalEntries.length > 0) {
                                                return (
                                                    <div className={`${styles.viewSection} ${styles.viewSectionLast}`}>
                                                        <div className={styles.viewSectionTitle}>Further Project Roles</div>
                                                        <div className={styles.detailsGroup}>
                                                            {memberWithHistory.historicalEntries.map((histEntry, idx) => {
                                                                const roleAbbr = getRoleAbbreviation(histEntry.rolle);
                                                                const histVonDate = formatDate(histEntry.aktivVon);
                                                                const histBisDate = histEntry.aktivBis ? formatDate(histEntry.aktivBis) : '';
                                                                return (
                                                                    <div key={idx} style={{ marginBottom: tokens.spacingVerticalS }}>
                                                                        <div className={styles.detailValue}>
                                                                            <strong>{roleAbbr}:</strong> {histVonDate} - {histBisDate}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}

                                        {!(() => {
                                            const memberWithHistory = deduplicatedMembers.find((m) => m.personalID === selectedMember.personalID);
                                            return memberWithHistory?.historicalEntries && memberWithHistory.historicalEntries.length > 0;
                                        })() && (
                                                <div className={`${styles.viewSection} ${styles.viewSectionLast}`} style={{ minHeight: 0 }} />
                                            )}

                                        <div className={styles.buttonGroup}>
                                            {canEditMembers && (
                                                <Button
                                                    icon={<Edit24Regular />}
                                                    onClick={handleEnterEditMode}
                                                    appearance="primary"
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                            {canEditMembers && (
                                                selectedMember.isAktiv && selectedMember.itemId ? (
                                                    <Button
                                                        icon={<Prohibited24Regular />}
                                                        onClick={handleDeactivateMember}
                                                        appearance="outline"
                                                        style={{ color: 'orange' }}
                                                    >
                                                        Deactivate
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        icon={<CheckmarkCircle24Regular />}
                                                        onClick={handleActivateMember}
                                                        appearance="outline"
                                                        style={{ color: 'green' }}
                                                    >
                                                        Activate
                                                    </Button>
                                                )
                                            )}
                                            {canEditMembers && (
                                                <Button
                                                    icon={<Delete24Regular />}
                                                    onClick={handleDeleteMember}
                                                    appearance="outline"
                                                    style={{ color: 'red' }}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                            <Button onClick={handleCloseDrawer}>Close</Button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {drawerMode === 'add' && (
                            <>
                                <div className={styles.formGroup}>
                                    <Label required>Search Team Member</Label>
                                    <Combobox
                                        placeholder="Enter at least 2 characters..."
                                        onOptionSelect={(_, option) => {
                                            const personalID = parseInt(option.optionValue || '0');
                                            const member = memberSearchResults.find((m) => m.personalID === personalID);
                                            if (member) {
                                                setSelectedSearchMember(member);
                                            }
                                        }}
                                        onChange={(ev) => {
                                            const value = (ev.target as HTMLInputElement).value;
                                            handleMemberSearch(value);
                                        }}
                                    >
                                        {memberSearchResults.map((member) => (
                                            <Option
                                                key={member.personalID}
                                                value={member.personalID.toString()}
                                                text={member.fullName || `${member.vorname} ${member.nachname}`}
                                            >
                                                {member.fullName || `${member.vorname} ${member.nachname}`}
                                            </Option>
                                        ))}
                                    </Combobox>
                                </div>

                                {selectedSearchMember && (
                                    <div className={styles.detailsGroup}>
                                        <div>
                                            <div className={styles.detailLabel}>Selected Member</div>
                                            <div className={styles.detailValue}>{selectedSearchMember.fullName || `${selectedSearchMember.vorname} ${selectedSearchMember.nachname}`}</div>
                                        </div>
                                    </div>
                                )}

                                <div className={styles.buttonGroup}>
                                    <Button onClick={handleSaveMemberAssignment} disabled={!selectedSearchMember} appearance="primary">
                                        Invite Member
                                    </Button>
                                    <Button onClick={handleCloseDrawer}>Cancel</Button>
                                </div>
                            </>
                        )}
                    </div>
                </DrawerBody>
            </Drawer>

        </div >
    );
};

const MitarbeiterProjektteam: React.FC<MitarbeiterProjektteamProps> = ({ project: projectProp }) => {
    const { project: contextProject, isLoading, error } = useProject();
    const project = projectProp || contextProject;

    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', minHeight: '300px' }}>
                <Spinner label="Projektteam wird geladen..." />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ color: 'red', padding: '20px' }}>
                {error}
            </div>
        );
    }

    return <MitarbeiterProjektteamContent project={project} />;
};

export default MitarbeiterProjektteam;
